import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Cover, StoreButtons, PublicHeader } from "./Showcase";
import { ReleaseBanner, Ribbon } from "./showcase/Release";
import { API } from "../lib/api";
import { Headphones } from "lucide-react";
import { TrailerPlayer } from "./showcase/Trailer";
import { RemindMe, NewsletterSignup } from "./showcase/Signup";
import { PublicSeriesTimelines } from "./showcase/SeriesTimelines";
import { PoemSamples, ReadPreview } from "./showcase/FormatSamples";
import { FormatBadge } from "./showcase/FormatBadge";

function AudioSample({ slug }) {
  return (
    <div className="panel p-4 flex flex-wrap items-center gap-4" data-testid="audio-sample">
      <div className="flex items-center gap-2"><div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-600 shadow-[0_0_18px_rgba(199,125,255,0.6)]"><Headphones size={18} className="text-white" /></div>
        <div><p className="font-display text-sm">Listen to a sample</p><p className="text-[11px] text-slate-500">Audiobook excerpt</p></div></div>
      <audio controls preload="none" src={`${API}/public/books/${slug}/audio-sample`} className="flex-1 min-w-[220px] h-10" data-testid="audio-sample-player" />
    </div>
  );
}

function Sample({ s }) {
  return (
    <section className="panel p-6 sm:p-10" data-testid="book-sample">
      <p className="eyebrow mb-2">Read a sample</p>
      <h3 className="font-display text-2xl mb-6">{s.title}</h3>
      <div className="manuscript text-slate-200 space-y-4 max-w-2xl">{s.paragraphs.map((p, i) => <p key={i}>{p.replace(/\*+/g, "")}</p>)}</div>
      {s.truncated && <p className="text-xs text-slate-500 mt-6">— end of sample —</p>}
    </section>
  );
}

function SeriesOrder({ b }) {
  if (b.series_order.length < 2) return null;
  return (
    <section data-testid="book-series-order">
      <p className="eyebrow mb-4">{b.series} · reading order</p>
      <div className="flex gap-4 overflow-x-auto scrollbar-thin pb-2">
        {b.series_order.map((x) => (
          <Link key={x.slug} to={`/authors/${x.slug}`} className={`shrink-0 w-28 ${x.slug === b.slug ? "opacity-100" : "opacity-70 hover:opacity-100"}`} data-testid={`series-order-${x.slug}`}>
            <div className={`aspect-[2/3] rounded overflow-hidden ${x.slug === b.slug ? "ring-2 ring-amber-400" : ""}`}><Cover b={x} className="w-full h-full" /></div>
            <p className="text-[11px] mt-2 text-slate-300">{x.book_number ? `Book ${x.book_number}` : ""}</p><p className="text-xs leading-tight">{x.title}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function ShowcaseBook() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/public/books/${slug}`).then((r) => setB(r.data)).catch(() => setMissing(true)); }, [slug]);
  if (missing) return <div className="min-h-screen"><PublicHeader back /><p className="text-center text-slate-400 py-24" data-testid="showcase-book-missing">This book isn't available.</p></div>;
  if (!b) return <div className="min-h-screen"><PublicHeader back /></div>;
  return (
    <div className="min-h-screen" data-testid="showcase-book-page">
      <PublicHeader back />
      <div className="relative overflow-hidden"><div className="smoke" />
        <section className="relative max-w-6xl mx-auto px-4 sm:px-8 py-14 grid md:grid-cols-[300px_1fr] gap-12">
          <div className="relative aspect-[2/3] rounded-lg overflow-hidden shadow-[0_0_50px_rgba(157,78,221,0.35)] max-w-[300px] mx-auto w-full"><Ribbon r={b.release} slug={b.slug} /><Cover b={b} className="w-full h-full" /><FormatBadge format={b.format} slug={b.slug} overlay /></div>
          <div className="space-y-0">
            <div className="mb-3"><FormatBadge format={b.format} slug={b.slug} /></div>
            {b.series && <p className="eyebrow mb-3" data-testid="book-series">{b.series}{b.book_number ? ` · Book ${b.book_number}` : ""}</p>}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight break-words" data-testid="book-title">{b.title}</h1>
            {b.subtitle && <p className="text-lg text-amber-200 mt-2">{b.subtitle}</p>}
            {b.author && <p className="text-slate-400 mt-3">by {b.author}</p>}
            <div className="flex gap-2 flex-wrap mt-5" data-testid="book-formats">{b.formats.map((f) => <span key={f} className="text-[11px] px-2.5 py-1 rounded-full border border-slate-700 text-slate-300">{f}</span>)}{b.genre && <span className="text-[11px] px-2.5 py-1 rounded-full bg-amber-900/40 text-amber-200">{b.genre}</span>}</div>
            {(b.description || b.blurb) && <p className="text-slate-300 mt-6 whitespace-pre-line leading-relaxed max-w-2xl" data-testid="book-description">{b.description || b.blurb}</p>}
            {b.release && <div className="mt-6"><ReleaseBanner r={b.release} />{b.release.status === "coming_soon" && <RemindMe slug={b.slug} />}</div>}
            {b.has_audio_sample && <div className="mt-6"><AudioSample slug={b.slug} /></div>}
            <ReadPreview b={b} />
            <div className="mt-8"><StoreButtons b={b} stores={b.stores} size="lg" /></div>
          </div>
        </section>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 pb-20 space-y-12">
        {b.has_trailer && (
          <section className="grid md:grid-cols-[minmax(0,380px)_1fr] gap-8 md:gap-12 items-center">
            <TrailerPlayer slug={b.slug} title={b.title} blurb={b.blurb} poster={b.has_cover ? `${API}/public/books/${b.slug}/cover` : null} />
            <div><p className="eyebrow mb-3">Watch the trailer</p><h2 className="font-display text-3xl sm:text-4xl leading-tight">Step into {b.title}</h2>
              {b.blurb && <p className="text-slate-300 mt-4 max-w-lg leading-relaxed">{b.blurb}</p>}</div>
          </section>
        )}
        {b.series_id ? <PublicSeriesTimelines only={b.series_id} current={b.slug} /> : <SeriesOrder b={b} />}
        {b.sample && <Sample s={b.sample} />}
        <PoemSamples b={b} />
      </div>
      <NewsletterSignup />
    </div>
  );
}
